/* eslint-disable no-plusplus */
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useProgramPanel } from 'contexts/programPanelContext';
import { useTranslation } from 'react-i18next';
import i18n, { getLanguageName, languageOptions } from 'locale';
import { Box, BoxSection } from 'components/_new/Box';
import { ModifyQuestionPayload, surveyApi, UpdateQuestionSettingsPayload } from 'api/survey';
import { Icon } from 'components/_new/Icon';
import { Link } from 'components/_new/Link';
import { Button } from 'components/_new/Button';
import { DialogConfirm } from 'components/_new/Dialog/DialogConfirm';
import { SurveyProgramQuestionBase } from 'types/Survey';
import { useUrlSearch } from 'utils/useUrlSearch';
import { useLocation, useNavigate } from 'react-router-dom';
import SubPageTitle from 'components/_common/panel/SubPageTitle';
import { capitalizeWord } from 'utils/helpers';
import { QuestionEditDialog } from './components/QuestionEditDialog';
import { QuestionPlacement } from './types';
import { StandardView } from './components/StandardView';
import { QuestionItem } from './components/QuestionItem';

export const ProgramFeedbackEditQuestions = () => {
	const { t } = useTranslation();
	const navigate = useNavigate();

	const { data: program, panelPath } = useProgramPanel();
	const programId = Number(program?.id);

	const { data, refetch, isLoading } = useQuery(
		['PROGRAM_PANEL_SURVEY_QUESTIONS', { programId }],
		() => surveyApi.panel.getAllForProgram(programId),
		{ enabled: true }
	);

	const { mutateAsync: updateQuestionSettingsMutateAsync, isPending: updateQuestionSettingsPending } = useMutation(
		({ questionId, data }: { questionId: number; data: UpdateQuestionSettingsPayload }) =>
			surveyApi.panel.updateQuestionSettings(programId, questionId, data)
	);

	const { mutateAsync: addNewQuestionMutateAsync, isPending: addNewQuestionPending } = useMutation(
		(data: ModifyQuestionPayload) => surveyApi.panel.addNewQuestion(programId, data)
	);

	const { mutateAsync: updateQuestionMutateAsync, isPending: updateQuestionPending } = useMutation(
		({ questionId, data }: { questionId: number; data: ModifyQuestionPayload }) =>
			surveyApi.panel.updateQuestion(programId, questionId, data)
	);

	const { mutateAsync: removeQuestionMutateAsync, isPending: removeQuestionPending } = useMutation(
		(questionId: number) => surveyApi.panel.removeQuestion(programId, questionId)
	);

	const localQuestionsState = useState<SurveyProgramQuestionBase[]>([]);
	const [localQuestions, setLocalQuestions] = localQuestionsState;
	const [editingQuestion, setEditingQuestion] = useState<SurveyProgramQuestionBase | null>(null);
	const [isNewQuestion, setIsNewQuestion] = useState<{ step: number; sequence: number; only: boolean } | null>(null);
	const [deleteConfirmState, setDeleteConfirmState] = useState<number | null>(null);

	const disableActions =
		updateQuestionSettingsPending || addNewQuestionPending || removeQuestionPending || updateQuestionPending;

	useEffect(() => {
		if (data) {
			setLocalQuestions(data);
		}
	}, [data]);

	// Edycja pytania
	const handleEditQuestion = useCallback(
		(questionId: number) => {
			const question = localQuestions.find((q) => q.id === questionId);
			if (question) {
				setEditingQuestion(question);
			}
		},
		[localQuestions]
	);

	const handleSaveQuestion = useCallback(
		(updatedQuestionData: ModifyQuestionPayload, questionId?: number) => {
			if (questionId) {
				updateQuestionMutateAsync({ questionId, data: updatedQuestionData }).then(() => {
					refetch();
				});
			}

			if (isNewQuestion) {
				addNewQuestionMutateAsync({
					...updatedQuestionData,
					inProgramStep: isNewQuestion.step,
					inProgramSequence: isNewQuestion.sequence,
					only: isNewQuestion.only,
				}).then(() => {
					refetch();
				});
			}

			setIsNewQuestion(null);
			setEditingQuestion(null);
		},
		[addNewQuestionMutateAsync, isNewQuestion, refetch, updateQuestionMutateAsync]
	);

	const handleCloseEditDialog = useCallback(() => {
		setEditingQuestion(null);
		setIsNewQuestion(null);
	}, []);

	const handleDeleteQuestion = useCallback(
		(id: number) => {
			const questionToDelete = localQuestions.find((q) => q.id === id);
			if (questionToDelete) {
				// Pytania systemowe (programId === null) - wyłączanie bez potwierdzenia
				if (questionToDelete.programId === null) {
					removeQuestionMutateAsync(id).then((status) => {
						setLocalQuestions((prev) => {
							const otherQuestions = prev.filter((q) => q.id !== questionToDelete.id);
							if (status === 'excluded') {
								return [
									...otherQuestions,
									{
										...questionToDelete,
										isExcluded: true,
									},
								];
							}
							if (status === 'reactivated') {
								return [
									...otherQuestions,
									{
										...questionToDelete,
										isExcluded: false,
									},
								];
							}
							return otherQuestions;
						});
					});
				} else {
					// Pytania programowe - wymagane potwierdzenie
					setDeleteConfirmState(questionToDelete.id);
				}
			}
		},
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[localQuestions]
	);

	const handleConfirmDelete = useCallback(() => {
		if (deleteConfirmState) {
			removeQuestionMutateAsync(deleteConfirmState).then(() => {
				setLocalQuestions((prev) => prev.filter((q) => q.id !== deleteConfirmState));
				setDeleteConfirmState(null);
			});
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [deleteConfirmState, removeQuestionMutateAsync]);

	const handleCloseDeleteConfirm = useCallback(() => {
		setDeleteConfirmState(null);
	}, []);

	const questionStats = useMemo(() => {
		const systemQuestions = localQuestions.filter((q) => q.programId === null);
		const programQuestions = localQuestions.filter((q) => q.programId !== null);

		const usedLanguages = Array.from(
			new Set(
				programQuestions
					.filter(({ translations }) => translations.length > 0)
					.map(({ translations }) => translations.map(({ languageCode }) => languageCode))
					.flat()
			)
		);
		const translationStats = languageOptions
			.filter(({ value }) => usedLanguages.includes(value))
			.map(({ value }) => {
				const translatedQuestions = programQuestions.filter((q) =>
					q.translations?.some((t) => t.languageCode === value)
				);
				return {
					languageCode: value,
					translated: translatedQuestions.length,
					total: programQuestions.length,
					questionIds: translatedQuestions.map(({ id }) => id),
				};
			});

		return {
			all: localQuestions.length,
			systemActive: systemQuestions.filter((q) => !q.isExcluded).length,
			systemExcluded: systemQuestions.filter((q) => q.isExcluded).length,
			systemRequired: systemQuestions.filter((q) => q.required).length,
			program: programQuestions.length,
			programRequired: programQuestions.filter((q) => q.required).length,
			programPeer: programQuestions.filter((q) => q.forApplicationRole === 'peer').length,
			programMentor: programQuestions.filter((q) => q.forApplicationRole === 'mentor').length,
			programMentee: programQuestions.filter((q) => q.forApplicationRole === 'mentee').length,
			translationStats,
		};
	}, [localQuestions]);

	const handleAddQuestion = (placement: QuestionPlacement) => {
		setIsNewQuestion(placement);
	};

	const {
		params: { filter, lang },
	} = useUrlSearch<{ filter: string | null; lang: string | null }>({ filter: null, lang: null });

	const filteredQuestions = useMemo(() => {
		if (!filter) return [];

		const systemQuestions = localQuestions.filter((q) => q.programId === null);
		const programQuestions = localQuestions.filter((q) => q.programId !== null);

		switch (filter) {
			case 'all':
				return localQuestions;
			case 'systemActive':
				return systemQuestions.filter((q) => !q.isExcluded);
			case 'systemExcluded':
				return systemQuestions.filter((q) => q.isExcluded);
			case 'systemRequired':
				return systemQuestions.filter((q) => q.required);
			case 'program':
				return programQuestions;
			case 'programRequired':
				return programQuestions.filter((q) => q.required);
			case 'programPeer':
				return programQuestions.filter((q) => q.forApplicationRole === 'peer');
			case 'programMentor':
				return programQuestions.filter((q) => q.forApplicationRole === 'mentor');
			case 'programMentee':
				return programQuestions.filter((q) => q.forApplicationRole === 'mentee');
			case 'translations':
				if (lang) {
					return programQuestions.filter(
						({ id }) =>
							!questionStats.translationStats
								.find(({ languageCode }) => languageCode === lang)
								?.questionIds.includes(id)
					);
				}
				return [];
			default:
				return [];
		}
	}, [filter, lang, localQuestions, questionStats]);

	const location = useLocation();

	return (
		<>
			<SubPageTitle
				title={t('organizationPanel.programFeedback')}
				additional={program?.displayName}
				right={() => (
					<Button
						label={t('programPanel.feedback.backToStatistics')}
						onClick={() => {
							navigate(`/${panelPath}/feedback`);
						}}
						variant="primary-outlined"
					/>
				)}
			/>
			<QuestionEditDialog
				visible={isNewQuestion !== null || editingQuestion !== null}
				question={editingQuestion}
				onHide={handleCloseEditDialog}
				onSave={handleSaveQuestion}
				disableActions={disableActions}
			/>

			<DialogConfirm
				visible={deleteConfirmState !== null}
				title={t('programPanel.feedback.deleteQuestion.title')}
				message={t('programPanel.feedback.deleteQuestion.message')}
				onConfirm={handleConfirmDelete}
				onHide={handleCloseDeleteConfirm}
			/>

			<p>{t('programPanel.feedback.desc')}</p>

			<div className="flex flex-column gap-4 lg:flex-row">
				<div className="flex-1 flex flex-column gap-3 pb-3">
					{filter ? (
						<Box variant="white-bordered">
							<BoxSection
								header={
									<div className="flex flex-row justify-content-between align-items-center w-full">
										<span>
											{filter === 'translations' && lang ? (
												<>
													{t(`programPanel.feedback.missingTranslations`, {
														languageName: getLanguageName(lang, i18n.language),
													})}{' '}
													({filteredQuestions.length})
												</>
											) : (
												<>
													{t(`programPanel.feedback.stats.${filter}`)} (
													{filteredQuestions.length})
												</>
											)}
										</span>
										<Link to={{ search: '' }}>
											<Button
												icon="times"
												variant="primary-text"
												variantSize="sm"
												label={t('actions.cancel')}
											/>
										</Link>
									</div>
								}
							>
								<div className="flex flex-column gap-3">
									{filteredQuestions.length > 0 ? (
										filteredQuestions.map((question, index) => (
											<QuestionItem
												key={question.id}
												no={false}
												question={question}
												dragHandleProps={false}
												moveProps={{ up: false, down: false }}
												onEdit={handleEditQuestion}
												onDelete={handleDeleteQuestion}
												disableActions={disableActions}
											/>
										))
									) : (
										<div className="text-center text-gray p-3">
											{t('programPanel.feedback.noFilteredQuestions')}
										</div>
									)}
								</div>
							</BoxSection>
						</Box>
					) : (
						<StandardView
							localQuestionsState={localQuestionsState}
							disableActions={disableActions}
							onAddQuestion={handleAddQuestion}
							onEdit={handleEditQuestion}
							onDelete={handleDeleteQuestion}
							onUpdateQuestionSettings={updateQuestionSettingsMutateAsync}
							isLoading={isLoading}
						/>
					)}
				</div>
				<div className="flex flex-column gap-2 w-300-auto2">
					<Box variant="white-bordered">
						<BoxSection header={t('programPanel.feedback.stats.questions')}>
							<div className="flex flex-row gap-2 justify-content-between">
								<div>
									<Icon name="question-circle" iconStyle="solid" className="text-purple" />
									<span className="ml-2">{t('programPanel.feedback.stats.allQuestions')}</span>
								</div>
								<Link variant="purple" to={{ pathname: location.pathname }}>
									<span className="font-bold">{questionStats.all}</span>
								</Link>
							</div>
						</BoxSection>

						<BoxSection header={t('programPanel.feedback.stats.system')}>
							<div className="flex flex-row gap-2 justify-content-between">
								<div>
									<Icon name="question-circle" iconStyle="solid" className="text-purple" />
									<span className="ml-2">{t('programPanel.feedback.stats.active')}</span>
								</div>
								<Link variant="purple" to={{ search: 'filter=systemActive' }}>
									<span className="font-bold">{questionStats.systemActive}</span>
								</Link>
							</div>
							<div className="flex flex-row gap-2 justify-content-between">
								<div>
									<Icon name="eye-slash" iconStyle="solid" className="text-gray" />
									<span className="ml-2">{t('programPanel.feedback.stats.excluded')}</span>
								</div>
								<Link variant="purple" to={{ search: 'filter=systemExcluded' }}>
									<span className="font-bold">{questionStats.systemExcluded}</span>
								</Link>
							</div>
							<div className="flex flex-row gap-2 justify-content-between">
								<div>
									<Icon name="star-of-life" iconStyle="solid" className="text-danger" />
									<span className="ml-2">{t('programPanel.feedback.stats.required')}</span>
								</div>
								<Link variant="purple" to={{ search: 'filter=systemRequired' }}>
									<span className="font-bold">{questionStats.systemRequired}</span>
								</Link>
							</div>
						</BoxSection>

						<BoxSection header={t('programPanel.feedback.stats.program')}>
							<div className="flex flex-row gap-2 justify-content-between">
								<div>
									<Icon name="question-circle" iconStyle="solid" className="text-purple" />
									<span className="ml-2">{t('programPanel.feedback.stats.allProgram')}</span>
								</div>
								<Link variant="purple" to={{ search: 'filter=program' }}>
									<span className="font-bold">{questionStats.program}</span>
								</Link>
							</div>
							<div className="flex flex-row gap-2 justify-content-between">
								<div>
									<Icon name="star-of-life" iconStyle="solid" className="text-danger" />
									<span className="ml-2">{t('programPanel.feedback.stats.required')}</span>
								</div>
								<Link variant="purple" to={{ search: 'filter=programRequired' }}>
									<span className="font-bold">{questionStats.programRequired}</span>
								</Link>
							</div>
							<div className="flex flex-row gap-2 justify-content-between">
								<div>
									<Icon name="users" iconStyle="solid" className="text-purple" />
									<span className="ml-2">{t('programPanel.feedback.stats.programPeer')}</span>
								</div>
								<Link variant="purple" to={{ search: 'filter=programPeer' }}>
									<span className="font-bold">{questionStats.programPeer}</span>
								</Link>
							</div>
							<div className="flex flex-row gap-2 justify-content-between">
								<div>
									<Icon name="user-graduate" className="text-blue-500" />
									<span className="ml-2">{t('programPanel.feedback.stats.programMentor')}</span>
								</div>
								<Link variant="purple" to={{ search: 'filter=programMentor' }}>
									<span className="font-bold">{questionStats.programMentor}</span>
								</Link>
							</div>
							<div className="flex flex-row gap-2 justify-content-between">
								<div>
									<Icon name="user-tie" className="text-purple-500" />
									<span className="ml-2">{t('programPanel.feedback.stats.programMentee')}</span>
								</div>
								<Link variant="purple" to={{ search: 'filter=programMentee' }}>
									<span className="font-bold">{questionStats.programMentee}</span>
								</Link>
							</div>

							<div className="text-md mb-1 font-semibold">
								{t('programPanel.feedback.stats.translations')}:
							</div>
							{questionStats.translationStats.map(({ languageCode, translated, total }) => {
								const langOption = languageOptions.find((o) => o.value === languageCode);
								const Flag = langOption?.Flag;
								const name = capitalizeWord(
									getLanguageName(languageCode, i18n.language) || languageCode
								);
								return (
									<div key={languageCode} className="flex flex-row gap-2 justify-content-between">
										<div className="flex flex-row align-items-center">
											{Flag && <Flag className="w-1" />}
											<span className="ml-2">{name}</span>
										</div>
										<Link
											variant={translated < total ? 'danger' : 'success'}
											to={{ search: `filter=translations&lang=${languageCode}` }}
											disabled={translated >= total}
											noUnderline={translated >= total}
										>
											<span className="font-bold">
												{translated}/{total}
											</span>
										</Link>
									</div>
								);
							})}
						</BoxSection>
					</Box>
				</div>
			</div>
		</>
	);
};
